import { DataAccessHelpersService } from "./data-access-helpers.service";
import { Injectable, NgZone } from "@angular/core";
import { AngularFireAuth } from "@angular/fire/auth";
import {
  AngularFirestore,
  AngularFirestoreDocument,
} from "@angular/fire/firestore";
import { MatSnackBar } from "@angular/material";
import { ActivatedRouteSnapshot, Router } from "@angular/router";
import * as firebase from "firebase";
import { Observable, of } from "rxjs";
import { map, switchMap, take } from "rxjs/operators";
import { translate } from "@ngneat/transloco";
import { SnackBarComponent } from "../../pages/snack-bar/snack-bar.component";
import { environment } from "../../../environments/environment";
import { ParcAccessService } from "./parc-access.service";
@Injectable({
  providedIn: "root",
})
export class AuthService {
  user: Observable<IUser>;
  redirectRouteSnapshot: ActivatedRouteSnapshot;
  API_ENDPOINT = environment.API_ENDPOINT;
  roles: any;

  headers = { "Content-Type": "application/json" };

  /**
   * Get auth data, then get firestore user document and set it to local storage
   * @param afAuth AngularFirestore authentification
   * @param afs AngularFirestore services
   * @param dhs DataAccessHelpersService
   * @param snackBar Snackbar
   * @param router Navigation and routing
   */
  constructor(
    private afAuth: AngularFireAuth,
    private afs: AngularFirestore,
    private dhs: DataAccessHelpersService,
    public snackBar: MatSnackBar,
    private router: Router,
    private pas: ParcAccessService,
    public zone: NgZone
  ) {
    this.user = this.afAuth.authState.pipe(
      switchMap((user) => {
        if (user) {
          return this.afs
            .doc<IUser>(`users/${user.uid}`)
            .snapshotChanges()
            .pipe(
              map((a) => {
                this.pas.getRoles().subscribe((data) => {
                  this.roles = data.DepotWeb;

                  return this.roles;
                });

                const data = a.payload.data() as any;
                const id = a.payload.id;
                const path = a.payload.ref.path;

                localStorage.setItem(
                  "user",
                  JSON.stringify({ id, path, ...data })
                );

                localStorage.setItem(
                  "userTimezone",
                  Intl.DateTimeFormat().resolvedOptions().timeZone
                );

                if (!localStorage.getItem("language")) {
                  localStorage.setItem("language", "fr");
                }
                return { id, path, ...data };
              })
            );
        } else {
          this.clearAppStorage();
          return of(false);
        }
      })
    );
  }

  getIdToken() {
    return this.afAuth.auth.currentUser.getIdToken(true);
  }

  /**
   * Trusted-device token: persisted in localStorage so it is scoped to THIS
   * browser / device (and keyed per account). Its absence is what forces 2FA
   * again on a new device, a new browser, or a different account.
   */
  private deviceTokenKey(email: string): string {
    return `2fa_device_token:${(email || '').trim().toLowerCase()}`;
  }

  getDeviceToken(email: string): string | null {
    try {
      return localStorage.getItem(this.deviceTokenKey(email));
    } catch {
      return null;
    }
  }

  setDeviceToken(email: string, token: string): void {
    try {
      if (token) {
        localStorage.setItem(this.deviceTokenKey(email), token);
      }
    } catch {
      /* storage unavailable - user will just be asked for 2FA again */
    }
  }

  clearDeviceToken(email: string): void {
    try {
      localStorage.removeItem(this.deviceTokenKey(email));
    } catch {
      /* ignore */
    }
  }

  /**
   * Purge the app's localStorage on logout WITHOUT losing the trusted-device
   * tokens: a "remember this device for 6 months" mark must survive sign-out.
   */
  clearAppStorage(): void {
    try {
      const preserved: { [key: string]: string } = {};
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.indexOf('2fa_device_token:') === 0) {
          preserved[key] = localStorage.getItem(key);
        }
      }
      localStorage.clear();
      Object.keys(preserved).forEach(k => localStorage.setItem(k, preserved[k]));
    } catch {
      try { localStorage.clear(); } catch { /* ignore */ }
    }
  }

  /**
   * Send 2FA OTP code via email - uses backend.
   * The backend always regenerates the code and routes it based on the user's
   * `hasMailbox` flag (user's own email when true, admins sharing a parc when
   * false). Returns:
   *   - alreadyVerified: this device is trusted, no 2FA needed
   *   - sentToAdmins: the code was sent to admins (hasMailbox === false)
   *   - adminEmails:   recipient admin emails (only when sentToAdmins)
   */
  async send2FACode(email: string): Promise<{ alreadyVerified: boolean; sentToAdmins?: boolean; adminEmails?: string[] }> {

    const lang = 'en';
    const idToken = await this.getIdToken();
    const deviceToken = this.getDeviceToken(email);
    const response = await fetch(`${this.API_ENDPOINT}/sendAndSave2FACode`, {
      headers: {
        Authorization: idToken,
        'Content-Type': 'application/json',
      },
      method: 'POST',
      body: JSON.stringify({ email, lang, deviceToken }),
    });

    if (!response.ok) {
      const error = await response.json();
      const err: any = new Error(error.error || 'Failed to send 2FA code');
      err.code = error.code;
      throw err;
    }

    const data = await response.json();
    return {
      alreadyVerified: data.alreadyVerified || false,
      sentToAdmins: data.sentToAdmins === true,
      adminEmails: data.adminEmails || [],
    };
  }

  /**
   * Verify 2FA code - uses backend.
   * On success, stores the returned trusted-device token for this browser.
   * On failure, throws an Error whose `.code` / `.attemptsRemaining` carry the
   * structured backend result (e.g. 'TOO_MANY_ATTEMPTS').
   */
  async verify2FACode(email: string, code: string): Promise<boolean> {

    try {
      const idToken = await this.getIdToken();
      const response = await fetch(`${this.API_ENDPOINT}/verify2FACode`, {
        headers: {
          Authorization: idToken,
          'Content-Type': 'application/json',
        },
        method: 'POST',
        body: JSON.stringify({ email, code }),
      });

      const data = await response.json();

      if (!response.ok) {
        const err: any = new Error(data.error || 'Verification failed');
        err.code = data.code;
        err.attemptsRemaining = data.attemptsRemaining;
        throw err;
      }

      if (data.deviceToken) {
        this.setDeviceToken(email, data.deviceToken);
      }

      return true;

    } catch (error) {
      console.error('❌ Verification error:', error.message);
      throw error;
    }
  }

  /**
   * Check if user needs 2FA on THIS device/browser.
   * Returns true if 2FA is needed, false if this device was verified < 6 months ago.
   */
  async needs2FA(email: string): Promise<boolean> {
    const idToken = await this.getIdToken();
    const deviceToken = this.getDeviceToken(email);
    const response = await fetch(`${this.API_ENDPOINT}/check2FANeeded`, {
      headers: {
        Authorization: idToken,
        'Content-Type': 'application/json',
      },
      method: 'POST',
      body: JSON.stringify({ email, deviceToken }),
    });

    if (!response.ok) {
      // If check fails, default to requiring 2FA for security
      return true;
    }

    const data = await response.json();
    return data.needs2FA === true;
  }

  /**
   * @deprecated Use send2FACode — the backend now routes the code based on the
   * user's `hasMailbox` flag and regenerates it on every call.
   */
  async send2FAToAdmin(email: string): Promise<{ adminEmails: string[]; parcNames: any; adminParcs: any }> {
    const lang = 'en';
    const idToken = await this.getIdToken();
    const response = await fetch(`${this.API_ENDPOINT}/send2FAToAdmin`, {
      headers: {
        Authorization: idToken,
        'Content-Type': 'application/json',
      },
      method: 'POST',
      body: JSON.stringify({ email, lang }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to send code to admin');
    }

    const data = await response.json();
    return {
      adminEmails: data.adminEmails || [],
      parcNames: data.parcNames || {},
      adminParcs: data.adminParcs || {}
    };
  }

  /**
   * Send a verification email when register
   */
  doVerifyEmail(): Promise<void> {
    return this.afAuth.auth.currentUser.sendEmailVerification();
  }
  /**
   * Get the current user from firestore
   */
  currentUser(): firebase.User {
    return this.afAuth.auth.currentUser;
  }
  /**
   * Sign in with email and password
   * @param email Email
   * @param password Password
   */
  doEmailLogin(
    email: string,
    password: string
  ): Promise<firebase.auth.UserCredential> {
    return this.afAuth.auth.signInWithEmailAndPassword(email, password);
  }

  /**
   * Register user by email and password
   * @param email Email
   * @param password password
   */
  doRegisterEmailUser(
    email: string,
    password: string
  ): Promise<firebase.auth.UserCredential> {
    return this.afAuth.auth.createUserWithEmailAndPassword(email, password);
  }
  /**
   * Send password reset to a given email address
   * @param email Email address
   */
  doResetPassword(email: string): Promise<void> {
    return this.afAuth.auth.sendPasswordResetEmail(email);
  }
  /**
   * Send user information to server for registration in firestore authentication
   * @param formData User infos
   */



  createUser(formData: FormData): Promise<Response> {

    formData.forEach((value, key) => {
      console.log(`${key}: ${value}`);
    });

    return this.getIdToken()
      .then((idToken) => {
        return fetch(`${this.API_ENDPOINT}/createUser`, {
          headers: { Authorization: idToken },
          method: "POST", // *GET, POST, PUT, DELETE, etc
          body: formData, // body data type must match "Content-Type" header
        });
      })
      .catch((err) => Promise.reject(err));
  }
  addUser(payload: any): Promise<Response> {

  return this.getIdToken()
    .then((idToken) => {

      return fetch(`${this.API_ENDPOINT}/createUserSelfcare`, {

        method: "POST",

        headers: {
          Authorization: idToken,
          "Content-Type": "application/json"
        },

        body: JSON.stringify({
          body: payload   // 👈 IMPORTANT
        })

      });

    })
    .catch((err) => Promise.reject(err));
}

  /**
   * Send user information to server for updating user credentials
   * @param requestBody
   */
  updateUser(requestBody: Partial<IUser>): Promise<void> {
        return this.getIdToken()
      .then((idToken) => {
        return fetch(`${this.API_ENDPOINT}/updateUser`, {
          headers: { ...this.headers, Authorization: idToken },
          method: "POST", // *GET, POST, PUT, DELETE, etc
          body: JSON.stringify(requestBody), // body data type must match "Content-Type" header
        })
          .then((res) => {
            // this.snackBar.openFromComponent(SnackBarComponent, {
            //   data: {
            //     message: translate("Utilisateur Modifié avec succès"),
            //     icon: "check_circle_outline",
            //   },
            //   panelClass: ["style-success"],
            //   duration: 2000,
            // });
          })
          .catch((err) => {
            this.snackBar.openFromComponent(SnackBarComponent, {
              data: {
                message:
                  translate(`Erreur lors du modification`) + `${err.message}`,
                icon: "block",
              },
              panelClass: ["style-error"],
              duration: 2000,
            });
          });
      })
      .catch((err) => Promise.reject(err));
  }

  editUser(uid,data) {
    
    return this.afs.collection("users").doc(uid).update(
     
     data
    );
  }
  /**
   * Get all users that belongs to a given parc param
   * @param value Parc id
   */
  getAllUsers(value: string): Observable<IUser[]> {
    return this.dhs.getCollectionWhere(`users`, [
      { left: "allParcs", operator: "array-contains", right: value },
    ]);
  }
  getAllUsersOnce() {
    return this.dhs.getCollectionOnce(`users`);
  }
  /**
   * Disconnect user from session
   */
  doSignout(): Promise<void> {
    this.clearAppStorage();
    return this.afAuth.auth.signOut();
  }
  /**
   * Use Google API to sign up
   */
  doGoogleLogin(): Promise<void> {
    const oAuthProvider = new firebase.auth.GoogleAuthProvider();
    return this.oAuthLogin(oAuthProvider);
  }
  /**
   * Use Facebook API to sign up
   */
  doFacebookLogin(): Promise<void> {
    const oAuthProvider = new firebase.auth.FacebookAuthProvider();
    return this.oAuthLogin(oAuthProvider);
  }

  private oAuthLogin(provider) {
    return this.afAuth.auth
      .signInWithPopup(provider)
      .then((credential) => {
        this.updateUserData(credential.user);
        const commands = this.redirectRouteSnapshot.url;
        const queryParams = this.redirectRouteSnapshot.queryParams;
        this.zone.run(() => {
          this.router.navigate(commands, { queryParams });
        });
      })
      .catch((e) => {
        this.snackBar.openFromComponent(SnackBarComponent, {
          data: {
            message: translate("Impossible de vous connecter à votre compte"),
            icon: "block",
          },
          panelClass: ["style-error"],
          duration: 2000,
        });
      });
  }
  /**
   * Update User data
   * @param user User data
   */
  updateUserData(user: firebase.User) {
    // Sets user data to firestore on student-login
    const userRef: AngularFirestoreDocument<any> = this.afs.doc(
      `users/${user.uid}`
    );
    return userRef.set(user);
  }
  /**
   * Update user's profile
   * @param userId User identifier
   * @param data User data
   */
  updateUserInDb(userId: string, data: Partial<IUser>) {
    return this.afs.doc(`users/${userId}`).update(data);
  }
  updateUserByEmail(email: string, data: Partial<IUser>) {
    return this.afs
      .collection<IUser>('users', ref => ref.where('email', '==', email))
      .snapshotChanges()
      .pipe(take(1))
      .toPromise()
      .then(actions => {
        if (!actions.length) {
          throw new Error(`No user found with email: ${email}`);
        }
        const doc = actions[0].payload.doc.ref;
        return doc.update(data);
      });
  }
  /**
   * Disconnect user and redirect to Login page
   */
  signOut(): void {
    this.afAuth.auth.signOut().then(() => {
      this.clearAppStorage();
      this.zone.run(() => {
        this.router.navigate(["/login"]);
      });
    });
  }
  /**
   * Return all Firestore users
   */
  getUsersByType(): Promise<IUser[]> {
    return this.dhs.getCollectionOnce("users");
  }
  /**
   * Returns a promise of user with the same given email address
   * @param email Email address
   */
  getUserByEmail(
    email: string
  ): Promise<
    firebase.firestore.QuerySnapshot<firebase.firestore.DocumentData>
  > {
    return this.afs.collection("users").ref.where("email", "==", email).get();
  }
  // edit user as admin
  handleUserEdit(user, uid) {
    const userRef: AngularFirestoreDocument<any> = this.afs.doc(`users/${uid}`);
    return userRef.update(user);
  }
  /* Send reset password Via Mail to user */
  sendResetPasswordViaMail(email, userId) {
    const requestBody = {
      email: email,
    };
    return this.getIdToken()
      .then((idToken) => {
        return fetch(`${this.API_ENDPOINT}/resetPassword`, {
          headers: {
            Authorization: idToken,
            "Content-Type": "application/json",
          },
          method: "POST", // *GET, POST, PUT, DELETE, etc
          body: JSON.stringify(requestBody), // body data type must match "Content-Type" header
        }).then(async (res) => {
          // update user
          if (res.status === 200) {
            await this.updateUserInDb(userId, { resetPasswordSent: true });
            return { response: true };
          }
        });
      })
      .catch((err) => Promise.reject(err));
  }

  // send welcome email
  sendWelcomeEmail(email, displayName, userType, userId) {
    const requestBody = {
      userEmail: email,
      name: displayName,
      userType: userType,
    };
    return this.getIdToken()
      .then((idToken) => {
        return fetch(`${this.API_ENDPOINT}/welcomeMail`, {
          headers: {
            Authorization: idToken,
            "Content-Type": "application/json",
          },
          method: "POST", // *GET, POST, PUT, DELETE, etc
          body: JSON.stringify(requestBody), // body data type must match "Content-Type" header
        }).then(async (res) => {
          if (res.status === 200) {
            // update user
            await this.updateUserInDb(userId, { welcomeEmailSent: true });
            return { response: true };
          }
        });
      })
      .catch((err) => Promise.reject(err));
  }
  sendWelcomeEmailDepot(email, name, userType, lng) {
    const requestBody = {
      userEmail: email,
      name: name,
      userType: userType,
      lng: lng,
    };

    
    return this.getIdToken()
      .then((idToken) => {
        return fetch(`${this.API_ENDPOINT}/welcomeMailSelfcare`, {
          headers: {
            Authorization: idToken,
            "Content-Type": "application/json",
          },
          method: "POST", // *GET, POST, PUT, DELETE, etc
          body: JSON.stringify(requestBody), // body data type must match "Content-Type" header
        }).then(async (res) => {
          if (res.status === 200) {
            // update user
           // await this.updateUserInDb(userId, { welcomeEmailSent: true });
            return { response: true };
          }
        });
      })
      .catch((err) => Promise.reject(err));
  }
  
  sentResetLink(email: string, app): Promise<string> {
    const requestBody = { userEmail: email, app };
    return this.getIdToken()
      .then((idToken) => {
        return fetch(`${this.API_ENDPOINT}/sentResetLink`, {
          headers: {
            Authorization: idToken,
            "Content-Type": "application/json",
          },
          method: "POST",
          body: JSON.stringify(requestBody),
        });
      })
      .then(async (res) => {
        if (res.status === 200) {
          const data = await res.json();
          return data.link;
        } else {
          throw new Error("Failed to get reset link");
        }
      });
  }

  SendResetPassword(name: string, email: string, app: string): Promise<string> {
    const requestBody = { name, email, app };
    return fetch(`${this.API_ENDPOINT}/SendResetPassword`, {
      headers: {
        "Content-Type": "application/json",
      },
      method: "POST",
      body: JSON.stringify(requestBody),
    })
      .then(async (res) => {
        if (res.status === 200) {
          const data = await res.json();
          return data.link;
        } else {
          const text = await res.text();
          throw new Error(`Failed to get reset link: ${text}`);
        }
      });
  }

  
  SendResetPasswordAdmin(name: string, email: string, app: string): Promise<string> {
    const requestBody = { name, email, app };
    return fetch(`${this.API_ENDPOINT}/SendResetPassword`, {
      headers: {
        "Content-Type": "application/json",
      },
      method: "POST",
      body: JSON.stringify(requestBody),
    })
      .then(async (res) => {
        if (res.status === 200) {
          await this.updateUserByEmail(email, { resetPasswordSent: true });
          const data = await res.json();
          return data.link;
        } else {
          const text = await res.text();
          throw new Error(`Failed to get reset link: ${text}`);
        }
      });
  }
}
